import { call, put, SagaGenerator, select, take, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Attachment } from "@com.mgmtp.a12.dataservices/dataservices-access";
import {
	EngineState,
	EngineStore,
	Events,
	Commands,
	ModelSelectors,
	UiStateSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { findElementByFormModelPath, FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DocumentModel, DocumentServiceFactory, EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { createCustomNotificationAction } from "../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { AttachmentDto, EmailDto, PathWrapperDto } from "../../../types/email";
import { saveActivity } from "../../../utilities/activityUtils";
import { assertTrue } from "../../../utilities/assert";
import { setFocusToHTMLElement } from "../../../utilities/domUtils";
import {
	convertEntityInstancePathToString,
	entityInstancePathFromString,
	getValueFromEntityInstancePath,
	getValueFromModelPath,
	getValuesFromModelPath,
	isSubPath,
	mergedPaths,
	PathValueWrapper
} from "../../../utilities/pathHelpers";
import { isArrayOf, isAttachment, isDate, isDocument, isObject, isString } from "../../../utilities/typeGuardUtils";
import { executeWithCompleteValidation, executeWithoutValidation, validate } from "../../../utilities/validationUtils";
import {
	EMAIL_ABSENDEDATUM,
	EMAIL_ANHANG,
	EMAIL_BETREFF,
	EMAIL_EMPFAENGER,
	EMAIL_EMPFAENGER_TYP,
	EMAIL_NACHRICHT,
	EMAIL_VERSAND
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, syncAntragsActivityData } from "../../../utilities/workflowUtils";
import {
	AnnotatedInput,
	FormModelComponentCollector,
	FormModelUtils
} from "../../../workaround/internal/form-model-utils";
import { TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { sendEmail } from "../../../utilities/rpcHelper";
import { selectEnvironmentData } from "../../../app/slices/environmentData/selectors";
import { ModelWalker } from "../../../workaround/internal/form-model-walker";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

import { emailConfirmation, sendEmailAction, SendEmailPayload } from "../actions";
import { isEmailValidationFailedError } from "../emailValidation";

const logger = LoggerFactory.getLogger("modulf");

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * Is triggered when the event "event_send_mail" is triggered through a button click.
 */
export function* sendEmailSaga(): SagaGenerator<void> {
	yield* takeLatest(sendEmailAction.match, handle);
}

function* handle(action: Action<SendEmailPayload>): SagaGenerator<void> {
	const engineState = yield* select(FormEngineSelectors.engineState(action.payload.activityId));
	if (!engineState) {
		logger.error("Couldn't get EngineState");
		yield* call(createTechnicalErrorNotification, action.payload.activityId);
		return;
	}

	yield* call(handleSendEmail, action, engineState);

	const formModel = ModelSelectors.formModel()(engineState);
	const pathElements = FormModelUtils.findPathElementsByFormModelPath(formModel, action.payload.buttonPath);
	const eventButton = pathElements[pathElements.length - 1];

	if (FormModelUtils.isIdNamedInstance(eventButton)) {
		setFocusToHTMLElement("a12-" + eventButton.id);
	}
}

function* handleSendEmail(action: Action<SendEmailPayload>, engineState: EngineState) {
	const { activityId } = action.payload;
	const documentModel = ModelSelectors.documentModel()(engineState);
	const formModel = ModelSelectors.formModel()(engineState);

	try {
		const screenState = UiStateSelectors.currentScreenLocation()(engineState);
		const emailDtoObject = yield* call(extractEmailDto, action, formModel, screenState);
		if (!emailDtoObject) {
			return;
		}

		yield* call(executeBusy, function* (): SagaGenerator<void> {
			yield* call(executeWithoutValidation, activityId, function* (): SagaGenerator<void> {
				const data = yield* select(ActivitySelectors.data(activityId));
				if (!FormActivity.Data.SingleDocumentData.isInstance(data)) {
					throw new Error("Data is not suitable!");
				}
				const { modelId, ...document } = data.document;
				const formattedDocument = documentService.formatDates(document, documentModel);

				const updatedDocument = yield* call(sendEmail, data.document.id, formattedDocument, emailDtoObject);

				yield* setSendDateValue(updatedDocument.document, documentModel, activityId, emailDtoObject);

				yield* call(syncAntragsActivityData);

				if (isInRepeat(screenState, formModel)) {
					yield* put(
						FormEngineActions.event({
							activityId,
							engineEvent: Events.Repeat.leaveDetachedRepeatRow({ cancel: false })
						})
					);
				}
				yield* call(saveActivity, activityId);
			});
		});
	} catch (e) {
		logger.error(e);
		yield* call(handleEmailError, activityId, e);
		return;
	}
}

function extractEmailsAsStrings(
	emailEmpfaenger: PathValueWrapper,
	data: FormActivity.Data.SingleDocumentData
): string[] {
	const emailAddresses = emailEmpfaenger.length > 0 ? resolveEmailValue(emailEmpfaenger, data) : [];
	if (!isArrayOf(isString)(emailAddresses)) {
		throw new Error(`Value from field for ${EMAIL_EMPFAENGER} (Bcc) is not a string.`);
	}
	return emailAddresses;
}

function extractEmailAddresses(
	emailPathWrappers: Record<string, PathValueWrapper>,
	data: FormActivity.Data.SingleDocumentData
) {
	const emailEmpfaenger = emailPathWrappers[EMAIL_EMPFAENGER];
	if (emailEmpfaenger.length === 0) {
		throw new Error(`At least one value for ${EMAIL_EMPFAENGER} must be provided.`);
	}
	const emailEmpfaengerTyp = emailPathWrappers[EMAIL_EMPFAENGER_TYP];

	let emailEmpfaengerAn: PathValueWrapper = [];
	const emailEmpfaengerCc: PathValueWrapper = [];
	const emailEmpfaengerBcc: PathValueWrapper = [];
	if (emailEmpfaengerTyp.length === 0) {
		emailEmpfaengerAn = emailEmpfaenger;
	} else {
		const emailEmpfaengerTypes = resolveEmailValue(emailPathWrappers[EMAIL_EMPFAENGER_TYP], data);
		for (let i = 0; i < emailEmpfaengerTypes.length; i++) {
			switch (emailEmpfaengerTypes[i]) {
				case "BCC":
					emailEmpfaengerBcc.push(emailEmpfaenger[i]);
					break;
				case "CC":
					emailEmpfaengerCc.push(emailEmpfaenger[i]);
					break;
				default:
					emailEmpfaengerAn.push(emailEmpfaenger[i]);
			}
		}
	}

	const emailCcAddresses = extractEmailsAsStrings(emailEmpfaengerCc, data);
	const emailBccAddresses = extractEmailsAsStrings(emailEmpfaengerBcc, data);
	const emailAnAddresses = extractEmailsAsStrings(emailEmpfaengerAn, data);

	return { emailCcAddresses, emailBccAddresses, emailAnAddresses };
}

function* extractEmailDto(
	action: Action<SendEmailPayload>,
	formModel: FormModel,
	currentScreenState: EngineStore.ScreenState
): SagaGenerator<EmailDto | undefined> {
	const buttonAnnotations = yield* select(
		ModulfUiSelectors.selectButtonAnnotations(action.payload.activityId, action.payload.buttonPath)
	);
	const targetModuleAnnotation = buttonAnnotations.find(e => e.name === EMAIL_VERSAND);
	if (!targetModuleAnnotation) {
		throw new Error(
			`Cannot find target module because the annotation ${EMAIL_VERSAND}
			is missing on button: ${ModelPath.toString(action.payload.buttonPath)}`
		);
	}

	const targetModule = targetModuleAnnotation.value;
	if (!targetModule) {
		throw new Error(
			`Cannot find target module because the value of the annotation ${EMAIL_VERSAND}
			is missing on button: ${ModelPath.toString(action.payload.buttonPath)}`
		);
	}

	const targetModulSection = FormModelUtils.findPathElementsByFormModelPath(
		formModel,
		action.payload.buttonPath
	).find(e => FormModel.Section.isInstance(e) && e.name.endsWith(targetModule));
	if (!targetModulSection || !FormModel.Section.isInstance(targetModulSection)) {
		throw new Error(
			`Cannot find target module ${targetModule} from ${EMAIL_VERSAND} annotation
			from button: ${ModelPath.toString(action.payload.buttonPath)}`
		);
	}

	const formModelComponentCollector = new FormModelComponentCollector();
	new ModelWalker(formModelComponentCollector).acceptSection(targetModulSection);
	const annotatedComponents = formModelComponentCollector.getAnnotatedInputs();

	const screenPath = currentScreenState.path;

	const data = yield* select(ActivitySelectors.data(action.payload.activityId));
	if (!FormActivity.Data.SingleDocumentData.isInstance(data)) {
		throw new Error("Data is not suitable!");
	}

	const isValid = yield* call(validateEmailScreen, annotatedComponents, action.payload.activityId, screenPath);
	if (!isValid) {
		logger.error("The required e-mail data is not valid.");
		return undefined;
	}

	const emailPathWrappers = getEmailPathValueWrappers(annotatedComponents, screenPath, data, action.payload.bundId);

	if (!emailPathWrappers) {
		throw new Error("Cannot find required paths for the email delivery.");
	}

	for (const emailPathWrappersKey in emailPathWrappers) {
		if (
			emailPathWrappersKey !== EMAIL_EMPFAENGER_TYP &&
			Object.values(emailPathWrappers[emailPathWrappersKey]).length === 0
		) {
			throw new Error("Cannot find required paths for the email delivery.");
		}
	}

	const attachments: AttachmentDto[] | undefined = yield* call(getAttachments, annotatedComponents, screenPath, data);
	if (!attachments) {
		return;
	}

	if (action.payload.bundId) {
		const environmentData = yield* select(selectEnvironmentData);
		if (attachments.length > (environmentData.postfach?.maxFiles ?? 0)) {
			return;
		}
	}

	const emailAddresses = extractEmailAddresses(emailPathWrappers, data);

	const subject = resolveEmailValue(emailPathWrappers[EMAIL_BETREFF], data)[0];
	if (!isString(subject)) {
		throw new Error(`Value from field for ${EMAIL_BETREFF} is not a string.`);
	}

	const messageValues = resolveEmailValue(emailPathWrappers[EMAIL_NACHRICHT], data);
	const message = messageValues.filter(isString).join("\n");

	const sendDateControl = (yield* call(getAnnotatedInputsByAnnotation, annotatedComponents, EMAIL_ABSENDEDATUM))?.[0];
	if (!sendDateControl) {
		throw new Error(`Can not find field for ${EMAIL_ABSENDEDATUM}.`);
	}

	return {
		...emailAddresses,
		subject,
		message,
		attachments,
		sendDatePath: createPathWrapperDto(mergedPaths(sendDateControl.elementPath, screenPath)),
		isBundId: action.payload.bundId
	};
}

function* validateEmailScreen(
	annotatedComponents: AnnotatedInput[],
	activityId: string,
	screenPath: EntityInstancePath
): SagaGenerator<boolean> {
	const sendDateModelPath = getAnnotatedInputsByAnnotation(annotatedComponents, EMAIL_ABSENDEDATUM)?.[0].elementPath;
	if (!sendDateModelPath) {
		throw new Error(`Can not find field for ${EMAIL_ABSENDEDATUM}.`);
	}

	const sendDateEntityInstancePathString = convertEntityInstancePathToString(
		sendDateModelPath.map((p, idx) => ({
			elementName: p.elementName,
			index: screenPath[idx]?.index ?? 1
		}))
	);

	return yield* call(executeWithCompleteValidation<boolean>, activityId, function* () {
		const filterFunc = (ms: string) => ms !== sendDateEntityInstancePathString;
		const messageState = yield* call(validate, activityId, filterFunc, undefined, screenPath);
		return Object.entries(messageState).length === 0;
	});
}

export function getEmailPathValueWrappers(
	annotatedComponents: AnnotatedInput[],
	screenPath: EntityInstancePath,
	data: FormActivity.Data.SingleDocumentData,
	bundId?: boolean
): Record<string, PathValueWrapper> {
	const emailPathWrappers: Record<string, PathValueWrapper> = {
		[EMAIL_EMPFAENGER]: [],
		[EMAIL_EMPFAENGER_TYP]: [],
		[EMAIL_BETREFF]: [],
		[EMAIL_NACHRICHT]: []
	};

	Object.keys(emailPathWrappers).forEach(id => {
		const annotatedInputs = getAnnotatedInputsByAnnotation(annotatedComponents, id);
		if (id !== EMAIL_EMPFAENGER_TYP || annotatedInputs?.length !== 0) {
			for (const annotatedInput of annotatedInputs) {
				getValuesFromModelPath(annotatedInput.elementPath, data.document)
					.filter(r => (id === EMAIL_EMPFAENGER && bundId) || isSubPath(screenPath, r.path))
					.forEach(r => emailPathWrappers[id].push(r));
			}
		}
	});

	return emailPathWrappers;
}

function* getAttachments(
	annotatedComponents: AnnotatedInput[],
	screenPath: EntityInstancePath,
	data: FormActivity.Data.SingleDocumentData
): SagaGenerator<AttachmentDto[] | undefined> {
	const attachments: AttachmentDto[] = [];

	const attachmentArray = yield* call(getAttachmentsFromAnnotatedInput, annotatedComponents, data, screenPath);

	if (attachmentArray.length === 0) {
		yield* put(
			ActivityActions.create({
				initiatingActivityId: TASK_DETAIL_ACTIVITY_ID,
				activityDescriptor: {
					modal: "Bestaetigen"
				},
				data: {}
			})
		);

		const confirmationPayload = yield* take(emailConfirmation);

		if (!confirmationPayload.payload.isConfirmed) {
			return undefined;
		}
	}

	for (const attachment of attachmentArray) {
		if (!isAttachment(attachment)) {
			throw new Error(`Value from field for ${EMAIL_ANHANG} is not an attachment.`);
		}
		if (!attachment.attachment_id || !attachment.original_filename) {
			throw new Error(`Attachment id or attachment name is missing in attachment.`);
		}
		attachments.push({ attachmentId: attachment.attachment_id, attachmentFilename: attachment.original_filename });
	}

	return attachments;
}

function* getAttachmentsFromAnnotatedInput(
	controls: AnnotatedInput[],
	data: FormActivity.Data.SingleDocumentData,
	screenPath: EntityInstancePath
): SagaGenerator<Attachment[]> {
	const emailAnhangControls = yield* call(getAnnotatedInputsByAnnotation, controls, EMAIL_ANHANG);

	if (emailAnhangControls.length <= 0) {
		throw new Error(`Cannot find annotation ${EMAIL_VERSAND} with ${EMAIL_ANHANG} in form model.`);
	}

	const emailVersendenAttachmentPath = mergedPaths(emailAnhangControls[0].elementPath.slice(0, -1), screenPath);
	const documentVersendenGroup = getValueFromEntityInstancePath(emailVersendenAttachmentPath, data.document);

	if (!isObject(documentVersendenGroup) || !documentVersendenGroup.generiertMulti) {
		logger.info(`Cannot find value for attachments.`);
		return [];
	}
	const documentService = new DocumentServiceFactory().getDocumentService();
	return Array.isArray(documentVersendenGroup.generiertMulti)
		? documentVersendenGroup.generiertMulti
				.filter(path => isString(path.value) && path.value.startsWith("/root"))
				.map(path => entityInstancePathFromString(path.value))
				.map(path => documentService.getAssignedObject(data.document, path))
				.filter((pathValue): pathValue is Attachment => pathValue !== undefined)
		: [];
}

type EmailValue = string | string[] | Attachment | undefined;

function resolveEmailValue(
	pathValueWrappers: PathValueWrapper,
	data: FormActivity.Data.SingleDocumentData
): EmailValue[] {
	return pathValueWrappers.map(pathValueWrapper => {
		if (isString(pathValueWrapper.value) && pathValueWrapper.value.startsWith("/root")) {
			const pathValue = getValueFromModelPath(ModelPath.fromString(pathValueWrapper.value), data.document);
			if (!pathValue) {
				logger.info(`Cannot find value in document with path ${pathValueWrapper.path}.`);
			}
			return pathValue;
		}
		return pathValueWrapper.value;
	});
}

function getAnnotatedInputsByAnnotation(annotatedComponents: AnnotatedInput[], key: string): AnnotatedInput[] {
	return annotatedComponents.filter(e => e.annotations?.some(e => e.name === EMAIL_VERSAND && e.value === key));
}

function createPathWrapperDto(path: EntityInstancePath): PathWrapperDto {
	let pathString = "";
	const repetitions: number[] = [];
	for (const p of path) {
		pathString += "/" + p.elementName;
		repetitions.push(p.index);
	}

	return { path: pathString, repetitions };
}

function* setSendDateValue(
	document: object,
	documentModel: DocumentModel,
	activityId: string,
	emailDto: EmailDto
): SagaGenerator<void> {
	const parsedDocument = {
		...documentService.parseDates(document, documentModel),
		modelId: documentModel.header.id
	};
	assertTrue(isDocument(parsedDocument));

	const sendDatePath = createEntityInstancePath(emailDto.sendDatePath);
	const sendDate = getValueFromEntityInstancePath(sendDatePath, parsedDocument);

	if (!isDate(sendDate)) {
		throw new Error("Could not find send date.");
	}

	yield* put(
		FormEngineActions.event({
			activityId,
			engineEvent: Events.valueChange({
				path: sendDatePath,
				value: sendDate
			})
		})
	);
}

function createEntityInstancePath(pathWrapper: PathWrapperDto): EntityInstancePath {
	return pathWrapper.path
		.split("/")
		.slice(1)
		.map((e, idx) => ({ elementName: e, index: pathWrapper.repetitions[idx] }));
}

function isInRepeat(screenState: EngineStore.ScreenState, formModel: FormModel) {
	const repeatFormModelPath = ModelPath.parentPath(screenState.locationPath);
	const repeat = findElementByFormModelPath(formModel, repeatFormModelPath);
	return repeat && FormModel.Repeat.isInstance(repeat);
}

export { isEmailValidationFailedError } from "../emailValidation";

/**
 * Handles errors thrown during email sending.
 * When the backend reports a validation failure (EMAIL_VALIDATION_FAILED marker),
 * triggers full form validation so field-level error markers are shown to the user
 * — the same experience as when Speichern fails — and shows a validationFailed toast.
 * For all other errors a generic technical error toast is shown.
 */
export function* handleEmailError(activityId: string, e: unknown): SagaGenerator<void> {
	if (isEmailValidationFailedError(e)) {
		yield* put(
			FormEngineActions.command({
				activityId,
				engineEvent: Commands.validateFull()
			})
		);
		yield* call(createValidationFailedNotification, activityId);
	} else {
		yield* call(createTechnicalErrorNotification, activityId);
	}
}

function* createValidationFailedNotification(activityId: string): SagaGenerator<void> {
	yield* put(
		createCustomNotificationAction({
			activityId,
			message: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.error.email.validationFailed),
			severity: "error",
			group: "sendEmail"
		})
	);
}

function* createTechnicalErrorNotification(activityId: string): SagaGenerator<void> {
	yield* put(
		createCustomNotificationAction({
			activityId,
			message: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.error.email.technicalError),
			severity: "error",
			group: "sendEmail"
		})
	);
}
